import {
  SupportDialog,
  SupportDialogPopup,
  SupportDialogTrigger,
} from '@/components/support-dialog'
import { DuotoneIcon } from '@beeirl/ui/duotone-icon'
import { Separator } from '@beeirl/ui/separator'
import { cn } from '@beeirl/ui/utils/cn'
import React from 'react'

export function Tool({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      className={cn('mx-auto flex max-w-5xl flex-col justify-between px-8 pt-16 md:px-12 lg:px-16')}
      {...props}
    />
  )
}

export function ToolContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('mt-14', className)} {...props} />
}

export function ToolDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'mx-auto mt-1 max-w-xl text-balance text-center text-sm leading-6 text-gray-800 sm:mt-2 sm:text-base sm:leading-7',
        className
      )}
      {...props}
    />
  )
}

export function ToolHeading({ className, ...props }: React.ComponentProps<'h1'>) {
  return (
    <h1
      className={cn(
        'mx-auto w-full max-w-xl text-balance text-center text-2xl font-bold !leading-[1.2] tracking-tight text-gray-900 sm:text-3xl md:text-4xl',
        className
      )}
      {...props}
    />
  )
}

export function ToolSection({ className, ...props }: React.ComponentProps<'section'>) {
  return <section className={cn('mx-auto w-full max-w-3xl', className)} {...props} />
}

export function ToolSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn('mx-auto my-20 max-w-3xl bg-gray-100', className)}
      orientation="horizontal"
      {...props}
    />
  )
}

export function ToolSubheading({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2
      className={cn(
        'mx-auto w-full max-w-xl text-balance text-center text-2xl font-semibold !leading-[1.2] tracking-tight text-gray-900 sm:text-3xl md:text-4xl',
        className
      )}
      {...props}
    />
  )
}

function ToolCard({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & React.ComponentProps<'a'>) {
  const Comp = props.href ? 'a' : 'div'
  return (
    <Comp
      className={cn(
        'shadow-xs group col-span-12 flex cursor-pointer flex-col rounded-2xl border border-gray-200 p-1 md:col-span-6',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'flex flex-1 flex-col justify-between rounded-xl border border-gray-100 bg-gray-50 p-6 transition-colors duration-200 group-hover:border-gray-200',
          '[&>svg]:size-7 [&>svg]:transition-transform [&>svg]:duration-300 [&>svg]:ease-in-out [&>svg]:group-hover:scale-110'
        )}
      >
        {children}
      </div>
    </Comp>
  )
}

function ToolCardTitle({ className, ...props }: React.ComponentProps<'h4'>) {
  return <h3 className={cn('mt-4 text-base font-semibold text-gray-900', className)} {...props} />
}

function ToolCardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return <p className={cn('mt-3 text-pretty text-sm text-gray-800', className)} {...props} />
}

export function ToolList({
  className,
  tools = [
    'tiktok-comment-generator',
    'instagram-comment-generator',
    'reddit-post-template',
    'text-message-generator',
    'text-box-generator',
    'reddit-gif-downloader',
    'reddit-username-generator',
    'youtube-subscribe-link-generator',
    'youtube-timestamp-link-generator',
  ],
  ...props
}: React.ComponentProps<'div'> & {
  tools?: Array<
    | 'tiktok-comment-generator'
    | 'instagram-comment-generator'
    | 'reddit-post-template'
    | 'text-message-generator'
    | 'text-box-generator'
    | 'reddit-gif-downloader'
    | 'reddit-username-generator'
    | 'youtube-subscribe-link-generator'
    | 'youtube-timestamp-link-generator'
  >
}) {
  return (
    <div className={cn('grid grid-cols-12 gap-3', className)} {...props}>
      {tools.includes('tiktok-comment-generator') && (
        <ToolCard href="/tools/tiktok-comment-generator/">
          <DuotoneIcon.Comment2 className="text-accent-600" />
          <ToolCardTitle>TikTok Comment Generator</ToolCardTitle>
          <ToolCardDescription>
            Create custom TikTok comment reply stickers for your UGC content and video ads.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('instagram-comment-generator') && (
        <ToolCard href="/tools/instagram-comment-generator/">
          <DuotoneIcon.Comment className="text-pink-600" />
          <ToolCardTitle>Instagram Comment Generator</ToolCardTitle>
          <ToolCardDescription>
            Create custom Instagram comment reply stickers for your UGC content and video ads.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('text-message-generator') && (
        <ToolCard href="/tools/text-message-generator/">
          <DuotoneIcon.Comments className="text-green-600" />
          <ToolCardTitle>Fake Text Generator</ToolCardTitle>
          <ToolCardDescription>
            Create custom text messages for your text story videos.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('reddit-post-template') && (
        <ToolCard href="/tools/reddit-post-template/">
          <DuotoneIcon.Note className="text-orange-500" />
          <ToolCardTitle>Reddit Post Template</ToolCardTitle>
          <ToolCardDescription>
            Create custom Reddit posts for your Reddit story videos.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('text-box-generator') && (
        <ToolCard href="/tools/text-box-generator/">
          <DuotoneIcon.TextSquare className="text-purple-600" />
          <ToolCardTitle>Text Box Generator</ToolCardTitle>
          <ToolCardDescription>
            Create custom text boxes you can use on on all your social media platforms.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('reddit-gif-downloader') && (
        <ToolCard href="/tools/reddit-gif-downloader/">
          <DuotoneIcon.ImageDown className="text-orange-500" />
          <ToolCardTitle>Reddit GIF Downloader</ToolCardTitle>
          <ToolCardDescription>
            Download and save GIFs from your favorite Subreddits.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('reddit-username-generator') && (
        <ToolCard href="/tools/reddit-username-generator/">
          <DuotoneIcon.Bolt className="text-orange-500" />
          <ToolCardTitle>Reddit Username Generator</ToolCardTitle>
          <ToolCardDescription>
            Generate the best Reddit username ideas for your next Reddit account.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('youtube-subscribe-link-generator') && (
        <ToolCard href="/tools/youtube-subscribe-link-generator/">
          <DuotoneIcon.Bell className="text-red-600" />
          <ToolCardTitle>YouTube Subscribe Link Generator</ToolCardTitle>
          <ToolCardDescription>
            Generate a subscribe link to gain more subscribers for your YouTube channel.
          </ToolCardDescription>
        </ToolCard>
      )}
      {tools.includes('youtube-timestamp-link-generator') && (
        <ToolCard href="/tools/youtube-timestamp-link-generator/">
          <DuotoneIcon.Time className="text-red-600" />
          <ToolCardTitle>YouTube Timestamp Link Generator</ToolCardTitle>
          <ToolCardDescription>
            Generate a link to a specific point in time in your YouTube video.
          </ToolCardDescription>
        </ToolCard>
      )}
      <SupportDialog>
        <SupportDialogTrigger
          render={
            <ToolCard>
              <DuotoneIcon.Bulb className="text-yellow-500" />
              <ToolCardTitle>Request a Tool</ToolCardTitle>
              <ToolCardDescription>
                Suggest a new tool or feature you'd like to see added to Postfully.
              </ToolCardDescription>
            </ToolCard>
          }
        />
        <SupportDialogPopup defaultCategory="feedback" location="tools" />
      </SupportDialog>
    </div>
  )
}
